/**
 * Canonical theme token configuration.
 *
 * This file is the single source of truth for color values. The theme
 * <style> tag is generated from these objects (see components/theme/theme-style.tsx).
 * `globals.css` declares the Tailwind `@theme` token names that resolve to
 * `var(--theme-*)` at runtime — those resolve to the values below depending on
 * the active `data-theme` attribute.
 *
 * Adding a token: add it to `THEME_TOKEN_KEYS`, fill it in for both themes,
 * then expose it in `@theme` inside `globals.css` (so Tailwind generates a
 * utility for it).
 */

export const THEME_TOKEN_KEYS = [
  'background',
  'surface',
  'surface-elevated',
  'surface-muted',
  'border',
  'border-strong',
  'foreground',
  'foreground-muted',
  'foreground-subtle',
  'accent',
  'accent-foreground',
  'accent-soft',
  'accent-quiet',
  'accent-glow',
  'success',
  'warning',
  'danger',
  // `danger` is tuned to be legible *as text* on a surface, which in dark mode
  // makes it a light red — behind white it lands around 3.3:1 and fails AA.
  // Filled destructive controls (the ban buttons) use this deeper fill instead,
  // which clears 4.5:1 against white in both themes. Never use it for text.
  'danger-solid',
  'info',
  'ring',
  'table-stripe',
  'table-hover',
  // Categorical chart series — the item report's sparkline is the first
  // multi-series chart in the panel; every other chart primitive in
  // `components/report/charts.tsx` uses the single accent hue plus the
  // semantic success/warning/danger triad, not a dedicated series palette.
  'chart-s1',
  'chart-s2',
  'chart-s3',
  'chart-s4',
  // Slots 5–6 extend the same validated categorical order (magenta, green);
  // the gift-context split is the first figure that needs six identities.
  'chart-s5',
  'chart-s6',
] as const;

export type ThemeTokenKey = (typeof THEME_TOKEN_KEYS)[number];

export type ThemeTokens = Record<ThemeTokenKey, string> & {
  /** Body background — supports gradients. Stored separately because it's
   *  not a flat color and isn't a Tailwind color utility. */
  bodyBackground: string;
};

export const THEME_NAMES = ['light', 'dark'] as const;
export type ThemeName = (typeof THEME_NAMES)[number];

/** Includes 'system' which resolves to the user's OS preference. */
export type ThemeMode = ThemeName | 'system';

export const THEMES: Record<ThemeName, ThemeTokens> = {
  // Heather — see docs/design/heather-design-language.md §3, §10 (ADR 0026).
  light: {
    background: '#e9ebf2',
    surface: 'rgba(255, 255, 255, 0.88)',
    'surface-elevated': '#ffffff',
    'surface-muted': 'rgba(15, 23, 42, 0.04)',
    border: 'rgba(15, 23, 42, 0.16)',
    'border-strong': 'rgba(15, 23, 42, 0.26)',
    foreground: '#1a2235',
    'foreground-muted': '#5b6678',
    'foreground-subtle': '#9aa3b4',
    accent: '#6c4fa8',
    'accent-foreground': '#ffffff',
    'accent-soft': 'rgba(108, 79, 168, 0.08)',
    'accent-quiet': '#6c4fa8',
    'accent-glow': 'rgba(108, 79, 168, 0.08)',
    success: '#2f8f63',
    warning: '#b07628',
    danger: '#c4504e',
    'danger-solid': '#b8403e', // 5.5:1 with white
    info: '#4f6f9c',
    ring: 'rgba(108, 79, 168, 0.40)',
    'table-stripe': 'rgba(15, 23, 42, 0.04)',
    'table-hover': 'rgba(15, 23, 42, 0.08)',
    'chart-s1': '#2a78d6',
    'chart-s2': '#eb6834',
    'chart-s3': '#1baf7a',
    'chart-s4': '#eda100',
    'chart-s5': '#e87ba4',
    'chart-s6': '#008300',
    // Flat base comes from `background` (via background-color); this is only the
    // single soft top wash painted as the body background-image.
    bodyBackground:
      'radial-gradient(120% 120% at 50% -10%, rgba(108, 79, 168, 0.06) 0%, transparent 55%)',
  },
  dark: {
    background: '#0c1018',
    surface: 'rgba(22, 28, 40, 0.72)',
    'surface-elevated': '#161c28',
    'surface-muted': 'rgba(255, 255, 255, 0.035)',
    border: 'rgba(255, 255, 255, 0.085)',
    'border-strong': 'rgba(255, 255, 255, 0.16)',
    foreground: '#e6e9f0',
    'foreground-muted': '#97a1b3',
    'foreground-subtle': '#5d6678',
    accent: '#8f7ac2',
    'accent-foreground': '#ffffff',
    'accent-soft': 'rgba(143, 122, 194, 0.10)',
    'accent-quiet': '#a99cc9',
    'accent-glow': 'rgba(143, 122, 194, 0.10)',
    success: '#5aa37d',
    warning: '#c9974a',
    danger: '#e06a6a',
    'danger-solid': '#c9474a', // 4.65:1 with white, still vivid on #0c1018
    info: '#6f9cc9',
    ring: 'rgba(143, 122, 194, 0.45)',
    'table-stripe': 'rgba(255, 255, 255, 0.02)',
    'table-hover': 'rgba(255, 255, 255, 0.05)',
    'chart-s1': '#3987e5',
    'chart-s2': '#d95926',
    'chart-s3': '#199e70',
    'chart-s4': '#c98500',
    'chart-s5': '#d55181',
    'chart-s6': '#008300',
    bodyBackground:
      'radial-gradient(120% 120% at 50% -10%, rgba(124, 92, 196, 0.07) 0%, transparent 55%)',
  },
};

/** The mode applied when the user has expressed no preference. */
export const DEFAULT_THEME_MODE: ThemeMode = 'system';

/** The theme used when `system` is active and OS preference is unknown / light. */
export const SYSTEM_FALLBACK: ThemeName = 'light';

export const THEME_COOKIE = 'tp_theme';
